import React, { useState, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  TextField,
  InputAdornment,
  alpha,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router";
import { motion } from "framer-motion";
import SearchIcon from "@mui/icons-material/Search";
import StorageIcon from "@mui/icons-material/Storage";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import WebIcon from "@mui/icons-material/Web";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import DnsIcon from "@mui/icons-material/Dns";
import { DocumentHead } from "../../components/common/DocumentHead";
import { JsonLd } from "../../components/common/JsonLd";
import { ROUTES } from "../../config";
import {
  templateCategories,
  totalTemplateCount,
  type TemplateEntry,
} from "../../data/template-registry";

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  "frontend-backend": <WebIcon fontSize="large" />,
  "data-engineering": <StorageIcon fontSize="large" />,
  analytical: <AnalyticsIcon fontSize="large" />,
  observability: <MonitorHeartIcon fontSize="large" />,
  database: <DnsIcon fontSize="large" />,
};

const COMPLEXITY_CHIP_COLOR: Record<string, "success" | "warning" | "error"> = {
  low: "success",
  medium: "warning",
  high: "error",
};

function highlightMatch(text: string, query: string): boolean {
  if (!query) return true;
  return text.toLowerCase().includes(query.toLowerCase());
}

export const TemplateIndexPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");

  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return templateCategories;

    return templateCategories
      .map((cat) => ({
        ...cat,
        templates: cat.templates.filter(
          (t: TemplateEntry) =>
            highlightMatch(t.name, searchQuery) ||
            highlightMatch(t.description, searchQuery) ||
            highlightMatch(t.useCase, searchQuery) ||
            t.tags.some((tag) => highlightMatch(tag, searchQuery)) ||
            highlightMatch(t.language, searchQuery),
        ),
      }))
      .filter((cat) => cat.templates.length > 0);
  }, [searchQuery]);

  return (
    <Box sx={{ py: 8 }}>
      <DocumentHead
        title="Template Library | Rusha"
        description="Choose from curated, production-ready infrastructure templates. Full-stack web apps, data engineering pipelines, lakehouses, OLAP analytics, and more."
        path={ROUTES.TEMPLATES}
      />
      <JsonLd
        data={{
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://rusha.io",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Templates",
              item: "https://rusha.io/templates",
            },
          ],
        }}
      />
      <JsonLd
        data={{
          "@type": "ItemList",
          name: "Rusha Template Library",
          numberOfItems: totalTemplateCount,
          itemListElement: templateCategories.flatMap((cat) =>
            cat.templates.map((t, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: t.name,
              url: `https://rusha.io/templates/${t.slug}`,
            })),
          ),
        }}
      />
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography variant="h2" component="h1" gutterBottom fontWeight={800}>
            Template Library
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            {totalTemplateCount} production-ready templates across web
            applications, data engineering, and analytics. Each template
            includes full infrastructure specs, architecture diagrams, and
            deployment options.
          </Typography>

          {/* Search */}
          <TextField
            placeholder="Search templates by name, tag, or language..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
            sx={{ mb: 6 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </motion.div>

        {filteredCategories.length === 0 && (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="h2" component="h2" gutterBottom>
              No templates found
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Try a different search term.
            </Typography>
          </Box>
        )}

        <Grid container spacing={4}>
          {filteredCategories.map((category, catIndex) => (
            <Grid item xs={12} md={4} key={category.id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: catIndex * 0.1 }}
              >
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Box
                      sx={{
                        color: "primary.main",
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {CATEGORY_ICONS[category.id]}
                      <Typography variant="h2" component="h2" fontWeight={600}>
                        {category.name}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {category.description}
                    </Typography>
                    <Chip
                      label={`${category.templates.length} templates`}
                      size="small"
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                      }}
                    />
                  </CardContent>
                  <CardContent>
                    {category.templates.map((template: TemplateEntry) => (
                      <CardActionArea
                        key={template.slug}
                        onClick={() => navigate(`/templates/${template.slug}`)}
                        sx={{
                          mb: 1,
                          p: 1,
                          borderRadius: 1,
                          "&:hover": {
                            bgcolor: (theme) =>
                              alpha(theme.palette.primary.main, 0.04),
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            py: 1,
                          }}
                        >
                          <Box>
                            <Typography variant="body1" fontWeight={500}>
                              {template.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {template.tags.slice(0, 3).join(", ")}
                            </Typography>
                          </Box>
                          <Chip
                            label={template.complexity}
                            size="small"
                            color={
                              COMPLEXITY_CHIP_COLOR[template.complexity] ||
                              "default"
                            }
                            variant="outlined"
                          />
                        </Box>
                      </CardActionArea>
                    ))}
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default TemplateIndexPage;
